import type { UploadChannel, UploadReceipt } from './upload';
import { responseWords } from './problem-words';

/**
 * Setting a job up from what arrived (cpv6-parity S-20). A program may send documents through the API
 * into a job nobody has set up; Paige holds them unread and uncharged. The person who opens the job is
 * offered those documents as the setup's sample: the same "Add pages" the source cards start, except
 * that the files are already in Paige, so the API puts them into the job's engine itself
 * (`POST {job}/import/arrived`) and answers the receipt an upload answers. From there the engine's
 * setup from samples runs exactly as it does over files the person chose. A job in Setup has no
 * document types, so its "Add pages" is the setup's sample picker (folder-analysis.ts
 * `chooseRepresentativeFiles`), and that is the picker that must answer with them; a set-up job's
 * is `stageDocuments` (browser-captures.ts).
 */
export interface ArrivedSummary {
  pages: number;
  files: number;
  batches: number;
}

const nothing: ArrivedSummary = { pages: 0, files: 0, batches: 0 };

/** The event the offer raises; the drop zone answers it by starting "Add pages" (PageDropZone.tsx). */
export const arrivedEvent = 'paige:arrived-setup';

const freshFor = 10_000;
let offeredAt = 0;
let offeredFiles = 0;

/** How many files the offer last taken stood for. */
export const arrivedFileCount = (): number => offeredFiles;

const headersOf = (channel: UploadChannel): Record<string, string> =>
  channel.tenant ? { 'Paige-Tenant': channel.tenant } : {};

/** How much waits for the job's setup; nothing when the job is past its setup or the person may not set it up. */
export async function readArrived(channel: UploadChannel): Promise<ArrivedSummary> {
  try {
    const response = await fetch(`${channel.engineOrigin}/arrived`, {
      credentials: 'include',
      headers: headersOf(channel),
    });
    if (!response.ok) return nothing;
    const body = (await response.json()) as Partial<ArrivedSummary>;
    return { pages: body.pages ?? 0, files: body.files ?? 0, batches: body.batches ?? 0 };
  } catch {
    return nothing;
  }
}

/**
 * The person chose the arrived documents: the next "Add pages" takes them instead of opening the picker.
 * `files` is how many wait (the offer's own count), for the setup's "n files" before the API has moved them.
 */
export function offerArrived(files = 0): void {
  offeredAt = Date.now();
  offeredFiles = files;
  window.dispatchEvent(new Event(arrivedEvent));
}

/** Whether the arrived documents were just chosen; once, and only for a few seconds (as a drop is, pending-drop.ts). */
export function takeArrived(): boolean {
  const fresh = offeredAt > 0 && Date.now() - offeredAt <= freshFor;
  offeredAt = 0;
  return fresh;
}

interface ArrivedPart extends Partial<UploadReceipt> {
  remaining?: number;
}

/**
 * The held documents into the job's engine, a request at a time until none wait: each answer is an
 * upload's receipt and says how many pages still wait. The receipt joins the paths under the first
 * token, as an upload's does (upload.ts).
 */
export async function importArrived(channel: UploadChannel): Promise<UploadReceipt> {
  const receipt: UploadReceipt = { token: '', files: [], skipped: [] };
  for (;;) {
    const response = await fetch(`${channel.engineOrigin}/import/arrived`, {
      method: 'POST',
      credentials: 'include',
      headers: headersOf(channel),
    });
    if (!response.ok) throw new Error(await refusalOf(response));
    const part = (await response.json()) as ArrivedPart;
    receipt.token ||= part.token ?? '';
    receipt.files.push(...(part.files ?? []));
    receipt.skipped.push(...(part.skipped ?? []));
    // An answer that took nothing ends the loop whatever it says remains: it would take nothing again.
    if (!part.remaining || !part.files?.length) return receipt;
  }
}

async function refusalOf(response: Response): Promise<string> {
  return `The documents that arrived could not be added. ${await responseWords(response, 'Nothing was lost; try again in a minute.')}`;
}

/** The offer's one line: how much arrived, in the words the notice to the admins uses. */
export function arrivedLine(summary: ArrivedSummary): string {
  const pages = summary.pages === 1 ? '1 page' : `${summary.pages.toLocaleString()} pages`;
  return `${pages} arrived for this job and ${summary.pages === 1 ? 'is' : 'are'} waiting for its setup.`;
}
